import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Combobox } from '@/components/ui/combobox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { ProjectOptions } from '@/hooks/useProjectOptions'
import type { QuoteVersionDefaultsRequestDto } from './types'
import { getQuoteVersionDefaultAttributesV2, putQuoteVersionDefaultAttributesV2 } from './quotes-v2.api'

interface QuoteVersionBaseFinishesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  quoteId: string | null
  versionId: string | null
  options: ProjectOptions | null
  onSaved?: () => void | Promise<void>
}

type Attrs = Record<string, unknown>

const defaultAtr: Attrs = {
  hasFacade: true,
  hasCarcass: true,
  hasHandles: false,
  hasPlinth: false,
  facadeMaterial: 'Stratifié',
  carcassFinish: 'Mélaminé',
  facadeEdgeColorSameAsDoor: true,
  carcassEdgeColorSameAsDoor: true,
  carcassSideViewFinishSameAsDoor: true,
  plinthColorSameAsDoor: true,
}

function readBool(attrs: Attrs, key: string, fallback: boolean) {
  const v = attrs[key]
  return typeof v === 'boolean' ? v : fallback
}

function readString(attrs: Attrs, key: string, fallback = '') {
  const v = attrs[key]
  return typeof v === 'string' ? v : v == null ? fallback : String(v)
}

function readNumber(attrs: Attrs, key: string): number | null {
  const v = attrs[key]
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function QuoteVersionBaseFinishesDialog({
  open,
  onOpenChange,
  projectId,
  quoteId,
  versionId,
  options,
  onSaved,
}: QuoteVersionBaseFinishesDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attrs, setAttrs] = useState<Attrs>(defaultAtr)

  const materialOptions = useMemo(() => {
    const materials = options?.materials
    return {
      facade: (materials?.facade || []).map((x) => ({ value: x, label: x })),
      carcass: (materials?.carcass || []).map((x) => ({ value: x, label: x })),
    }
  }, [options])

  const finishOptions = useMemo(() => {
    return (options?.finishes || []).map((x) => ({ value: x, label: x }))
  }, [options])

  const enableFacade = readBool(attrs, 'hasFacade', false)
  const enableCarcass = readBool(attrs, 'hasCarcass', false)
  const enableHandles = readBool(attrs, 'hasHandles', false)
  const enablePlinth = readBool(attrs, 'hasPlinth', false)

  useEffect(() => {
    if (!open) return
    if (!quoteId || !versionId) return

    let isMounted = true

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)
        setAttrs(defaultAtr)
        const response = await getQuoteVersionDefaultAttributesV2(projectId, quoteId, versionId)
        if (!isMounted) return
        if (response.attributes && typeof response.attributes === 'object') {
          setAttrs(sanitizeAttrs(response.attributes))
        } else {
          setAttrs(defaultAtr)
        }
        
      } catch {
        if (!isMounted) return
        setAttrs(defaultAtr)
        setError('Erreur lors du chargement des finitions de base')
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [open, projectId, quoteId, versionId])

  const setAttr = (key: string, value: unknown) => {
    setAttrs((prev) => ({ ...prev, [key]: value }))
  }
// TODO: Remove plinthMaterialSameAsDoor from attributes in the backend as well
  const sanitizeAttrs = (raw: Attrs): Attrs => {
    const next = { ...raw }
    delete next['plinthMaterialSameAsDoor']
    return next
  }

  const handleSave = async () => {
    if (!quoteId || !versionId) return

    try {
      setIsSaving(true)
      setError(null)

      const request: QuoteVersionDefaultsRequestDto = {
        attributes: sanitizeAttrs(attrs),
      }

      await putQuoteVersionDefaultAttributesV2(projectId, quoteId, versionId, request)

      if (onSaved) {
        await onSaved()
      }

      onOpenChange(false)
    } catch {
      setError('Erreur lors de la sauvegarde des finitions de base')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Finitions de base (devis)</DialogTitle>
        </DialogHeader>

        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : null}

        <div className="max-h-[80vh] overflow-y-auto pr-1">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Chargement...</div>
          ) : (
            <div className="space-y-6">
            <div className="space-y-3">
              <div className="text-sm font-medium">Sections</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`qvbf-${versionId ?? 'unknown'}-hasFacade`}
                    checked={enableFacade}
                    onCheckedChange={(v) => setAttr('hasFacade', v === true)}
                  />
                  <Label htmlFor={`qvbf-${versionId ?? 'unknown'}-hasFacade`}>🚪 Façade</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`qvbf-${versionId ?? 'unknown'}-hasCarcass`}
                    checked={enableCarcass}
                    onCheckedChange={(v) => setAttr('hasCarcass', v === true)}
                  />
                  <Label htmlFor={`qvbf-${versionId ?? 'unknown'}-hasCarcass`}>📦 Caisse</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`qvbf-${versionId ?? 'unknown'}-hasHandles`}
                    checked={enableHandles}
                    onCheckedChange={(v) => setAttr('hasHandles', v === true)}
                  />
                  <Label htmlFor={`qvbf-${versionId ?? 'unknown'}-hasHandles`}>🖐️ Poignées</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`qvbf-${versionId ?? 'unknown'}-hasPlinth`}
                    checked={enablePlinth}
                    onCheckedChange={(v) => setAttr('hasPlinth', v === true)}
                  />
                  <Label htmlFor={`qvbf-${versionId ?? 'unknown'}-hasPlinth`}>🧱 Socle</Label>
                </div>
              </div>
            </div>

            {enableFacade ? (
              <div className="space-y-3">
                <div className="text-sm font-medium">🚪 Façade</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Matériau</Label>
                    <Combobox
                      options={materialOptions.facade}
                      value={readString(attrs, 'facadeMaterial', '')}
                      onChange={(v) => setAttr('facadeMaterial', v)}
                      allowCustomValue
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Coloris</Label>
                    <Input
                      className="h-9"
                      value={readString(attrs, 'facadeColor', '')}
                      onChange={(e) => setAttr('facadeColor', e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Coloris chant</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Checkbox
                        id={`qvbf-${versionId ?? 'unknown'}-facadeEdgeColorSameAsDoor`}
                        checked={readBool(attrs, 'facadeEdgeColorSameAsDoor', false)}
                        onCheckedChange={(v) => setAttr('facadeEdgeColorSameAsDoor', v === true)}
                      />
                      <Label
                        className="text-sm"
                        htmlFor={`qvbf-${versionId ?? 'unknown'}-facadeEdgeColorSameAsDoor`}
                      >
                        Identique aux portes
                      </Label>
                    </div>
                  </div>

                  {!readBool(attrs, 'facadeEdgeColorSameAsDoor', false) ? (
                    <div className="space-y-1">
                      <Label className="text-xs">Coloris chant</Label>
                      <Input
                        className="h-9"
                        value={readString(attrs, 'facadeEdgeColor', '')}
                        onChange={(e) => setAttr('facadeEdgeColor', e.target.value)}
                      />
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            ) : null}

            {enableCarcass ? (
              <div className="space-y-3">
                <div className="text-sm font-medium">📦 Caisse</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Finition</Label>
                    <Combobox
                      options={materialOptions.carcass}
                      value={readString(attrs, 'carcassFinish', '')}
                      onChange={(v) => setAttr('carcassFinish', v)}
                      allowCustomValue
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Coloris</Label>
                    <Input
                      className="h-9"
                      value={readString(attrs, 'carcassColor', '')}
                      onChange={(e) => setAttr('carcassColor', e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Coloris chant</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Checkbox
                        id={`qvbf-${versionId ?? 'unknown'}-carcassEdgeColorSameAsDoor`}
                        checked={readBool(attrs, 'carcassEdgeColorSameAsDoor', false)}
                        onCheckedChange={(v) => setAttr('carcassEdgeColorSameAsDoor', v === true)}
                      />
                      <Label
                        className="text-sm"
                        htmlFor={`qvbf-${versionId ?? 'unknown'}-carcassEdgeColorSameAsDoor`}
                      >
                        Identique aux portes
                      </Label>
                    </div>
                  </div>

                  {!readBool(attrs, 'carcassEdgeColorSameAsDoor', false) ? (
                    <div className="space-y-1">
                      <Label className="text-xs">Coloris chant</Label>
                      <Input
                        className="h-9"
                        value={readString(attrs, 'carcassEdgeColor', '')}
                        onChange={(e) => setAttr('carcassEdgeColor', e.target.value)}
                      />
                    </div>
                  ) : (
                    <div />
                  )}

                  <div className="space-y-1">
                    <Label className="text-xs">Côté visible</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Checkbox
                        id={`qvbf-${versionId ?? 'unknown'}-carcassSideViewFinishSameAsDoor`}
                        checked={readBool(attrs, 'carcassSideViewFinishSameAsDoor', false)}
                        onCheckedChange={(v) => setAttr('carcassSideViewFinishSameAsDoor', v === true)}
                      />
                      <Label
                        className="text-sm"
                        htmlFor={`qvbf-${versionId ?? 'unknown'}-carcassSideViewFinishSameAsDoor`}
                      >
                        Identique aux portes
                      </Label>
                    </div>
                  </div>

                  {!readBool(attrs, 'carcassSideViewFinishSameAsDoor', false) ? (
                    <div className="space-y-1">
                      <Label className="text-xs">Finition côté visible</Label>
                      <Input
                        className="h-9"
                        value={readString(attrs, 'carcassSideViewFinish', '')}
                        onChange={(e) => setAttr('carcassSideViewFinish', e.target.value)}
                      />
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            ) : null}

            {enableHandles ? (
              <div className="space-y-3">
                <div className="text-sm font-medium">🖐️ Poignées</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Modèle</Label>
                    <Input
                      className="h-9"
                      value={readString(attrs, 'handlesModel', '')}
                      onChange={(e) => setAttr('handlesModel', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Coloris</Label>
                    <Input
                      className="h-9"
                      value={readString(attrs, 'handlesColor', '')}
                      onChange={(e) => setAttr('handlesColor', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {enablePlinth ? (
              <div className="space-y-3">
                <div className="text-sm font-medium">🧱 Socle</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* <div className="space-y-1">
                    <Label className="text-xs">Matériau</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Checkbox
                        id={`qvbf-${versionId ?? 'unknown'}-plinthMaterialSameAsDoor`}
                        checked={readBool(attrs, 'plinthMaterialSameAsDoor', false)}
                        onCheckedChange={(v) => setAttr('plinthMaterialSameAsDoor', v === true)}
                      />
                      <Label
                        className="text-sm"
                        htmlFor={`qvbf-${versionId ?? 'unknown'}-plinthMaterialSameAsDoor`}
                      >
                        Identique aux portes
                      </Label>
                    </div>
                  </div> */}

                  <div className="space-y-1">
                    <Label className="text-xs">Hauteur (cm)</Label>
                    <Input
                      className="h-9"
                      type="number"
                      value={(() => {
                        const n = readNumber(attrs, 'plinthHeight')
                        return n == null ? '' : String(n)
                      })()}
                      min={0}
                      step={1}
                      onChange={(e) => {
                        const raw = e.target.value
                        if (raw === '') {
                          setAttr('plinthHeight', null)
                          return
                        }
                        const next = parseInt(raw, 10)
                        setAttr('plinthHeight', Number.isFinite(next) ? next : null)
                      }}
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Matériau</Label>
                    <Combobox
                      options={finishOptions}
                      value={readString(attrs, 'plinthMaterial', '')}
                      onChange={(v) => setAttr('plinthMaterial', v)}
                      allowCustomValue
                    />
                  </div>
                  
                  

                  <div className="space-y-1">
                    <Label className="text-xs">Coloris</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Checkbox
                        id={`qvbf-${versionId ?? 'unknown'}-plinthColorSameAsDoor`}
                        checked={readBool(attrs, 'plinthColorSameAsDoor', false)}
                        onCheckedChange={(v) => setAttr('plinthColorSameAsDoor', v === true)}
                      />
                      <Label className="text-sm" htmlFor={`qvbf-${versionId ?? 'unknown'}-plinthColorSameAsDoor`}>
                        Identique aux portes
                      </Label>
                    </div>
                  </div>

                  {!readBool(attrs, 'plinthColorSameAsDoor', false) ? (
                    <div className="space-y-1">
                      <Label className="text-xs">Coloris</Label>
                      <Input
                        className="h-9"
                        value={readString(attrs, 'plinthColor', '')}
                        onChange={(e) => setAttr('plinthColor', e.target.value)}
                      />
                    </div>
                  ) : (
                    <div />
                  )}

                  
                </div>
              </div>
            ) : null}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Fermer
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving || isLoading || !quoteId || !versionId}>
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
